import React, { useEffect, useMemo } from 'react';

import { Form, Input } from 'antd';
import styled from 'styled-components';

import AggregationTypePicker from 'components/lib/AggregationTypePicker';
import FilterDropdownHeader from 'components/lib/FilterDropdownHeader';
import { AGGREGATION_TYPE } from 'components/View/constants';

import CMASummaryRoomTypeFilterComparison from './CMASummaryRoomTypeFilterComparison';
import { CMASummaryRoomTypeFilterContext } from './CMASummaryRoomTypeFilterContext';
import type { CMASummaryRoomTypeFilterFormValues } from './CMASummaryRoomTypeFilterContext';
import CMASummaryRoomTypeFilterGrouping from './CMASummaryRoomTypeFilterGrouping';
import CMASummaryRoomTypeFilterSelection from './CMASummaryRoomTypeFilterSelection';
import { CMA_SUMMARY_INITIAL_FILTERS, CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS } from '../../constants';
import type { Option, RoomTypeGroupOption } from '../types';

type Props = {
  open: boolean;
  onClose: () => void;
  onApply: (payload: {
    selectedValues: number[];
    groupingValue: number;
    roomTypeFilterComparisonType: number;
    comparisonSqftRange: number;
    showClosestBeyondRange: boolean;
    roomTypesPriceDisplay: number;
  }) => void;
  options: Option[];
  groupedOptions: RoomTypeGroupOption[];
  selectedValues: number[];
  groupingValue: number;
  roomTypeComparisonType: number;
  comparisonSqftRange: number;
  showClosestBeyondRange: boolean;
  roomTypesPriceDisplay: number;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  .ant-form-item .ant-form-item-control-input {
    min-height: 0;
  }
`;

const CMASummaryRoomTypeFilterDropdown = ({
  open,
  onClose,
  onApply,
  options,
  groupedOptions,
  selectedValues,
  groupingValue,
  roomTypeComparisonType,
  comparisonSqftRange,
  showClosestBeyondRange,
  roomTypesPriceDisplay,
}: Props) => {
  const [form] = Form.useForm<CMASummaryRoomTypeFilterFormValues>();
  const priceDisplayValue =
    Form.useWatch('roomTypesPriceDisplay', form) ?? CMA_SUMMARY_INITIAL_FILTERS.roomTypesPriceDisplay;

  const orderedSelectedValues = useMemo(() => {
    const valueSet = new Set(selectedValues);
    return options.filter((option) => valueSet.has(option.value)).map((option) => option.value);
  }, [options, selectedValues]);

  const initialValues = useMemo(
    () => ({
      roomTypes: orderedSelectedValues,
      groupingValue,
      comparisonType: roomTypeComparisonType,
      comparisonSqftRange,
      showClosestBeyondRange,
      roomTypesPriceDisplay,
    }),
    [
      comparisonSqftRange,
      roomTypeComparisonType,
      groupingValue,
      orderedSelectedValues,
      roomTypesPriceDisplay,
      showClosestBeyondRange,
    ]
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [form, initialValues, open]);

  const getOrderedValues = (values: number[]) => {
    const valuesSet = new Set(values);
    return options.filter((option) => valuesSet.has(option.value)).map((option) => option.value);
  };

  const handleSubmit = (formValues: CMASummaryRoomTypeFilterFormValues) => {
    onApply({
      selectedValues: formValues.roomTypes || [],
      groupingValue: formValues.groupingValue,
      roomTypeFilterComparisonType: formValues.comparisonType,
      comparisonSqftRange: formValues.comparisonSqftRange,
      showClosestBeyondRange: formValues.showClosestBeyondRange,
      roomTypesPriceDisplay: formValues.roomTypesPriceDisplay ?? AGGREGATION_TYPE.AVG,
    });
    onClose();
  };

  const handleReset = () => {
    form.setFieldsValue({
      roomTypes: getOrderedValues(CMA_SUMMARY_INITIAL_FILTERS.roomTypes),
      groupingValue: CMA_SUMMARY_INITIAL_FILTERS.roomTypeGrouping,
      comparisonType: CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS.BY_ROOM_TYPE,
      comparisonSqftRange: CMA_SUMMARY_INITIAL_FILTERS.roomComparisonSqftRange,
      showClosestBeyondRange: CMA_SUMMARY_INITIAL_FILTERS.roomComparisonShowClosestBeyondRange,
      roomTypesPriceDisplay: CMA_SUMMARY_INITIAL_FILTERS.roomTypesPriceDisplay,
    });
  };

  return (
    <Form<CMASummaryRoomTypeFilterFormValues> form={form} initialValues={initialValues} onFinish={handleSubmit}>
      <CMASummaryRoomTypeFilterContext.Provider value={{ form, options, groupedOptions }}>
        <Container>
          <FilterDropdownHeader onReset={handleReset} />
          <Form.Item name="roomTypesPriceDisplay" hidden>
            <Input type="hidden" />
          </Form.Item>
          <AggregationTypePicker
            aggregationType={priceDisplayValue}
            label="PRICE DISPLAY"
            onSelect={(value: number) => form.setFieldsValue({ roomTypesPriceDisplay: value })}
          />
          <CMASummaryRoomTypeFilterComparison />
          <CMASummaryRoomTypeFilterGrouping />
          <CMASummaryRoomTypeFilterSelection />
        </Container>
      </CMASummaryRoomTypeFilterContext.Provider>
    </Form>
  );
};

export default CMASummaryRoomTypeFilterDropdown;
